import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost, apiPostBlob, apiPut } from '../../lib/api';
import { type Company, companySchema, type InvoiceTheme } from './companies.api';

/**
 * Modelul de factură al unei firme, dinspre client.
 *
 * ⚠️ **Listele închise vin de la server, nu scrise a doua oară aici.** `apps/crm` e
 * `scope:fe` și n-are voie să importe `@edin-inv/*`; iar o paletă copiată s-ar fi despărțit
 * de cea din modul la prima culoare adăugată, cu simptomul cel mai prost cu putință — un
 * pătrățel de o culoare în ecran și o bandă de altă culoare pe PDF.
 */
const layoutSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
});

const accentSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Culoarea, chiar cea cu care randează serverul. Ecranul o pune în pătrățel. */
  hex: z.string(),
});

const optionSchema = z.object({
  id: z.string(),
  label: z.string(),
  hint: z.string(),
});

const catalogSchema = z.object({
  layouts: z.array(layoutSchema),
  accents: z.array(accentSchema),
  options: z.array(optionSchema),
});

export type InvoiceModelCatalog = z.infer<typeof catalogSchema>;
export type InvoiceAccentOption = z.infer<typeof accentSchema>;

export const INVOICE_MODEL_CATALOG_QUERY_KEY = ['invoice-model-catalog'] as const;

/**
 * Catalogul e același pentru toate firmele și se schimbă doar cu un deploy, deci se ține
 * cât ține sesiunea: nu are de ce să fie cerut la fiecare deschidere de sertar.
 */
export const invoiceModelCatalogQueryOptions = () =>
  queryOptions({
    queryKey: INVOICE_MODEL_CATALOG_QUERY_KEY,
    queryFn: () => apiGet('/invoice-model', catalogSchema),
    staleTime: Number.POSITIVE_INFINITY,
  });

/**
 * `PUT`: modelul se **înlocuiește** în întregime, iar salvarea aceluiași model de două ori
 * trebuie să aibă același efect ca o dată.
 */
export function saveInvoiceModel(input: {
  companyId: string;
  theme: InvoiceTheme;
}): Promise<Company> {
  return apiPut(
    `/companies/${input.companyId}/invoice-model`,
    input.theme,
    z.object({ company: companySchema }),
  ).then((body) => body.company);
}

const previewSchema = z.object({
  html: z.string(),
  /** Chitanța de probă, cu același model (#187). Ecranul o pune sub factură. */
  receiptHtml: z.string(),
  /**
   * Firma e plătitoare, iar cota din setări lipsește sau e invalidă. Se arată **deasupra
   * colii, nu pe ea**; coala iese fără TVA, fiindcă proba nu inventează o cotă.
   */
  vatRateProblem: z.string().nullable(),
});

export type InvoiceModelPreview = z.infer<typeof previewSchema>;

/**
 * Factura de probă și chitanța ei, **ca HTML**, pentru previzualizarea din ecran.
 *
 * ⚠️ **Vin de la server, nu se desenează în React.** Sunt chiar documentele pe care le trimite
 * motorul la tipărire; un desen refăcut aici ar fi divergat de PDF la prima schimbare de
 * stil, iar „previzualizare care arată altfel decât documentul" e defectul de evitat.
 */
export function previewInvoiceModelHtml(input: {
  companyId: string;
  theme: InvoiceTheme;
  lineCount: number;
}): Promise<InvoiceModelPreview> {
  return apiPost(
    `/companies/${input.companyId}/invoice-model/previzualizare-html`,
    { theme: input.theme, lineCount: input.lineCount },
    previewSchema,
  );
}

/**
 * Aceeași factură de probă, descărcată ca PDF.
 *
 * Altfel operatorul ar fi trebuit să salveze ca să vadă ce alege, adică să strice modelul
 * în uz ca să încerce altul.
 */
export function previewInvoiceModel(input: {
  companyId: string;
  theme: InvoiceTheme;
  lineCount: number;
}): Promise<Blob> {
  return apiPostBlob(`/companies/${input.companyId}/invoice-model/previzualizare`, {
    theme: input.theme,
    lineCount: input.lineCount,
  });
}
